// A single looping audio voice (music or ambient) with its own gain, source,
// and crossfade state. SoundManager owns two instances — one per bus — so the
// near-identical music/ambient play+stop logic lives here exactly once.

import type { SoundDef } from './SoundManager.js';

/** Resolves the duck bus a voice should route through, with fallbacks. */
export type DuckTargetResolver = () => GainNode | null;

/** Resolves a sound id to its def. LoopVoice narrows to the file variant. */
export type SoundDefResolver = (id: string) => SoundDef | undefined;

export class LoopVoice {
  private gain: GainNode | null = null;
  private source: AudioBufferSourceNode | null = null;
  private currentId: string | null = null;

  constructor(
    private readonly defaultFadeMs: number,
    private readonly defaultGain: number,
    private readonly resolveDuckTarget: DuckTargetResolver,
  ) {}

  /** True if `soundId` is already playing on this voice. */
  isCurrent(soundId: string): boolean {
    return this.currentId === soundId && this.source !== null;
  }

  /** Play `soundId` with a crossfade from the current track. No-op if same id. */
  async play(
    ctx: AudioContext,
    buffers: Map<string, AudioBuffer>,
    getSoundDef: SoundDefResolver,
    soundId: string,
    fadeMs?: number,
  ): Promise<void> {
    if (this.isCurrent(soundId)) return;
    this.currentId = soundId;

    const def = getSoundDef(soundId);
    if (!def || def.type !== 'file') return;

    let buffer = buffers.get(def.url);
    if (!buffer) {
      try {
        const resp = await fetch(def.url);
        buffer = await ctx.decodeAudioData(await resp.arrayBuffer());
        buffers.set(def.url, buffer);
      } catch {
        return;
      }
    }

    const fade = fadeMs ?? this.defaultFadeMs;
    const oldGain = this.gain;
    const oldSource = this.source;

    const newGain = ctx.createGain();
    newGain.gain.value = 0;
    newGain.connect(this.resolveDuckTarget() ?? ctx.destination);

    const newSource = ctx.createBufferSource();
    newSource.buffer = buffer;
    newSource.loop = true;
    newSource.connect(newGain);
    newSource.start();

    const now = ctx.currentTime;
    const fadeSec = fade / 1000;
    if (oldGain) {
      oldGain.gain.setValueAtTime(oldGain.gain.value, now);
      oldGain.gain.linearRampToValueAtTime(0, now + fadeSec);
      if (oldSource) oldSource.stop(now + fadeSec + 0.05);
    }
    newGain.gain.setValueAtTime(0, now);
    newGain.gain.linearRampToValueAtTime(def.gain ?? this.defaultGain, now + fadeSec);

    this.gain = newGain;
    this.source = newSource;
  }

  /** Stop the current track with a fade-out. No-op if nothing playing. */
  stop(ctx: AudioContext, fadeMs?: number) {
    const fade = fadeMs ?? this.defaultFadeMs;
    const oldGain = this.gain;
    const oldSource = this.source;
    if (oldGain) {
      const now = ctx.currentTime;
      oldGain.gain.setValueAtTime(oldGain.gain.value, now);
      oldGain.gain.linearRampToValueAtTime(0, now + fade / 1000);
      if (oldSource) oldSource.stop(now + fade / 1000 + 0.05);
    }
    this.gain = null;
    this.source = null;
    this.currentId = null;
  }
}
